/**
 * The shell every page renders inside (contracts/web-surface.md §1).
 *
 * Four layers, outermost first. `ThemeProvider` decides the scheme and hands it
 * to Paper, so it has to sit above anything that reads a colour. `ApiProvider`
 * carries the TanStack Query client the signup mutation runs through.
 * `ErrorBoundary` is the last stop before a render crash reaches the visitor —
 * deliberately *outside* the shell `View` so it still catches a failure in the
 * header itself; its fallback loses the shell's background, but the theme
 * provider paints the same colour onto `<html>`, so the page still looks right.
 *
 * The shell `View` is a separate component rather than JSX inside `RootLayout`
 * because `useAppTheme` reads Paper's context: a hook cannot see a provider its
 * own component renders.
 */

import { createRootRoute, HeadContent, Outlet } from "@tanstack/react-router";
import { StyleSheet, View } from "react-native";
import ApiProvider from "@/components/api-provider";
import ErrorBoundary from "@/components/error-boundary";
import { SiteHeader } from "@/components/site-header";
import { ThemeProvider } from "@/components/theme-provider";
import { useAppTheme } from "@/theme/paper-theme";

/**
 * Mirrors the ERB layout's title/description pair (application.html.erb —
 * which carries the matching keep-in-step comment): the server renders them
 * into the document, and this head declaration is what restores them when a
 * visitor navigates back from a page that overrode the title.
 */
const PAGE_TITLE = "Sparkie — say it out loud, it's already a note";
const PAGE_DESCRIPTION =
  "Sparkie records what you say and transcribes it on your iPhone. What you " +
  "get back is a plain markdown file in a folder you control, with no " +
  "account needed and no cloud unless you turn it on.";

export const Route = createRootRoute({
  component: RootLayout,
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESCRIPTION },
    ],
  }),
});

function RootLayout() {
  return (
    <ThemeProvider>
      <HeadContent />
      <ApiProvider>
        <ErrorBoundary>
          <SiteShell />
        </ErrorBoundary>
      </ApiProvider>
    </ThemeProvider>
  );
}

/**
 * Full height and the scheme's background, and nothing else: the header owns
 * its own band, padding and bottom rule, and each page owns the padding of the
 * column it renders into. Spacing here would only fight both, which is why this
 * file reaches for no `@/theme/tokens` step.
 */
function SiteShell() {
  const theme = useAppTheme();

  return (
    <View style={[styles.shell, { backgroundColor: theme.colors.background }]}>
      <SiteHeader />
      <Outlet />
    </View>
  );
}

const styles = StyleSheet.create({
  shell: {
    // `flexGrow` rather than `flex: 1`, and the difference matters. Both fill
    // the window — the flex chain `#root` opens in `entrypoints/application.css`
    // sees to that — but `flex: 1` also sets `flexShrink: 1`, which squeezes the
    // shell back to one viewport on a page taller than the window and leaves the
    // background stopping at the fold. Growing alone keeps react-native-web's
    // `flexShrink: 0`, so the shell is the taller of the window and its content.
    flexGrow: 1,
  },
});
